import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { storage } from '../../config/firebase';
import { data as disciplinasData } from '../../components/disciplinas';

// Modelo: usuarios/{key} conserva `perfil` (rol de la cuenta) y suma
//   esExperto: true
//   perfilExperto: { descripcion, proyectos, disciplina, subdisciplina, tareaCalificada,
//                    competencias, disciplinasMultiples, disciplinasDetalle, urlfotoExperto }
// `perfil === 'Experto'` se sigue aceptando para registros anteriores a la migración.
export const esExperto = (usuario) => usuario?.esExperto === true || usuario?.perfil === 'Experto';

/** Campos de experto que vivían planos en el usuario antes del modelo `perfilExperto`. */
export const CAMPOS_EXPERTO_LEGACY = ['descripcion', 'descripcionExperto', 'proyectos', 'competencias', 'disciplina', 'subdisciplina', 'tareaCalificada', 'disciplinasMultiples', 'disciplinasDetalle', 'urlfotoExperto', 'photoUrl'];

/** Devuelve el perfil de experto de un usuario, sea del modelo nuevo o del anterior. */
export const perfilExpertoDe = (usuario) => {
	if (!usuario) return {};
	if (usuario.perfilExperto) return usuario.perfilExperto;
	return {
		descripcion: usuario.descripcion || usuario.descripcionExperto || '',
		proyectos: usuario.proyectos || '',
		competencias: usuario.competencias || {},
		disciplina: usuario.disciplina || usuario.competencias?.disciplina || '',
		subdisciplina: usuario.subdisciplina || usuario.competencias?.subdisciplina || '',
		tareaCalificada: usuario.tareaCalificada || usuario.competencias?.tareas || [],
		disciplinasMultiples: usuario.disciplinasMultiples || [],
		disciplinasDetalle: usuario.disciplinasDetalle || [],
		urlfotoExperto: usuario.urlfotoExperto || usuario.photoUrl || '',
	};
};

const unicos = (lista) => [...new Set(lista)];

/** Subdisciplinas que ofrecen las disciplinas elegidas. */
export const subdisciplinasDe = (disciplinas) => unicos(disciplinas.flatMap((d) => Object.keys(disciplinasData[d] || {})));

/** Tareas de todas las combinaciones disciplina/subdisciplina elegidas. */
export const tareasDe = (disciplinas, subdisciplinas) => unicos(disciplinas.flatMap((d) => subdisciplinas.flatMap((s) => disciplinasData[d]?.[s] || [])));

/** Estado del formulario a partir de un perfil de experto guardado. */
export const estadoDesdePerfilExperto = (usuario) => {
	const p = perfilExpertoDe(usuario);
	let disciplinas = [];
	let subdisciplinas = [];

	if (p.disciplinasMultiples?.length) {
		disciplinas = unicos(p.disciplinasMultiples.map((i) => i.disciplina));
		subdisciplinas = unicos(p.disciplinasMultiples.map((i) => i.subdisciplina));
	} else if (p.competencias?.disciplina) {
		disciplinas = [p.competencias.disciplina];
		subdisciplinas = p.competencias.subdisciplina ? [p.competencias.subdisciplina] : [];
	} else if (p.disciplina && p.subdisciplina) {
		disciplinas = p.disciplina.split(', ').filter((d) => d.trim());
		subdisciplinas = p.subdisciplina.split(', ').filter((s) => s.trim());
	}

	return {
		descripcion: p.descripcion === '-' ? '' : p.descripcion || '',
		proyectos: p.proyectos || '',
		disciplinas,
		subdisciplinas,
		urlfotoExperto: p.urlfotoExperto || '',
		foto: [],
	};
};

/** Arma el objeto `perfilExperto` que se guarda en la BD a partir del estado del formulario. */
export const construirPerfilExperto = ({ descripcion, proyectos, disciplinas, subdisciplinas, urlfotoExperto }) => {
	const combinaciones = disciplinas.flatMap((disciplina) =>
		subdisciplinas.filter((subdisciplina) => disciplinasData[disciplina]?.[subdisciplina]).map((subdisciplina) => ({ disciplina, subdisciplina, tareas: disciplinasData[disciplina][subdisciplina] }))
	);

	return {
		descripcion: descripcion || '-',
		proyectos: proyectos || '',
		// Primer elemento: formato original, que aún leen registros antiguos
		competencias: combinaciones[0] || {},
		disciplinasMultiples: combinaciones,
		disciplina: disciplinas.join(', '),
		subdisciplina: subdisciplinas.join(', '),
		tareaCalificada: tareasDe(disciplinas, subdisciplinas),
		disciplinasDetalle: disciplinas
			.map((disciplina) => ({ disciplina, subdisciplinas: subdisciplinas.filter((s) => disciplinasData[disciplina]?.[s]) }))
			.filter((item) => item.subdisciplinas.length > 0),
		urlfotoExperto: urlfotoExperto || '',
	};
};

/** Sube la foto del experto a Storage y devuelve su URL. */
export const subirFotoExperto = async (docId, archivo) => {
	const storageRef = ref(storage, `/${docId}/${archivo.name}`);
	const subida = await uploadBytesResumable(storageRef, archivo);
	return getDownloadURL(subida.ref);
};

/** Devuelve el mensaje de error del formulario de experto, o null si está completo. */
export const validarPerfilExperto = ({ disciplinas, subdisciplinas }) => (disciplinas.length === 0 || subdisciplinas.length === 0 ? 'Por favor selecciona al menos una disciplina y una subdisciplina para el experto' : null);
